/**
 * PATH: packages/job-hub/src/jobs/qa/qaHoldTimer.ts
 * FILE_ID: JOBHUB-QA-HOLD-TIMER-001
 * PURPOSE: 500ms press that still starts on a textarea or a button.
 */
import { useRef, type MouseEvent, type TouchEvent } from 'react'

export function useQaHoldTimer(onFire: (point: { clientX: number; clientY: number }) => void) {
  const hold = useRef<number | null>(null)
  const suppressUntil = useRef(0)
  const fired = useRef(false)
  const start = useRef<{ x: number; y: number } | null>(null)
  const fire = useRef(onFire)
  fire.current = onFire
  function disarm() {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = null
  }
  return {
    onTouchStart: (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!touch) return
      disarm()
      fired.current = false
      start.current = { x: touch.clientX, y: touch.clientY }
      const point = { clientX: touch.clientX, clientY: touch.clientY }
      hold.current = window.setTimeout(() => {
        fired.current = true
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
        suppressUntil.current = Date.now() + 750
        fire.current(point)
      }, 500)
    },
    onTouchEnd: (event: TouchEvent) => {
      if (fired.current) event.preventDefault()
      fired.current = false
      start.current = null
      disarm()
    },
    onTouchMove: (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!touch || !start.current) return
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/sheets/QaPhoneScopeSheet.tsx (armMenu) and packages/ui/src/mobile/useLongPress.ts (10px move tolerance)
      if (Math.hypot(touch.clientX - start.current.x, touch.clientY - start.current.y) > 10) disarm()
    },
    onTouchCancel: () => {
      fired.current = false
      start.current = null
      disarm()
    },
    onClickCapture: (event: MouseEvent) => {
      if (Date.now() > suppressUntil.current) return
      event.preventDefault()
      event.stopPropagation()
      suppressUntil.current = 0
    },
  }
}
